import { Box, Skeleton, Typography } from '@mui/material';
import CollectionsOutlinedIcon from '@mui/icons-material/CollectionsOutlined';
import { DashboardEventMediaPreviewItem } from './dashboardEventMediaSchemas';
import { useAuthenticatedImage } from '../hooks/useAuthenticatedImage';

interface EventMediaMosaicProps {
    previewMedia: DashboardEventMediaPreviewItem[];
}

const EventMediaThumb: React.FC<{ item: DashboardEventMediaPreviewItem; alt: string }> = ({ item, alt }) => {
    const { src, isLoading } = useAuthenticatedImage(item.url);
    const isImage = item.mime_type.startsWith('image/');

    if (!isImage) {
        return (
            <Box
                sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'action.hover',
                }}
            >
                <CollectionsOutlinedIcon color="action" fontSize="small" />
            </Box>
        );
    }

    if (isLoading) {
        return <Skeleton variant="rectangular" width="100%" height="100%" />;
    }

    if (!src) {
        return (
            <Box
                sx={{
                    width: '100%',
                    height: '100%',
                    bgcolor: 'action.hover',
                }}
            />
        );
    }

    return (
        <Box
            component="img"
            src={src}
            alt={alt}
            loading="lazy"
            sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
            }}
        />
    );
};

const EventMediaMosaic: React.FC<EventMediaMosaicProps> = ({ previewMedia }) => {
    if (previewMedia.length === 0) {
        return (
            <Box
                sx={{
                    width: '100%',
                    aspectRatio: '4 / 3',
                    borderRadius: 1.5,
                    border: '1px dashed',
                    borderColor: 'divider',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'background.default',
                }}
            >
                <Typography variant="caption" color="text.secondary">
                    Keine Vorschau
                </Typography>
            </Box>
        );
    }

    if (previewMedia.length === 1) {
        return (
            <Box sx={{ width: '100%', aspectRatio: '4 / 3', borderRadius: 1.5, overflow: 'hidden' }}>
                <EventMediaThumb item={previewMedia[0]} alt="Vorschaubild" />
            </Box>
        );
    }

    if (previewMedia.length === 2) {
        return (
            <Box
                sx={{
                    width: '100%',
                    aspectRatio: '4 / 3',
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 0.5,
                }}
            >
                {previewMedia.map((item) => (
                    <Box key={item.id} sx={{ borderRadius: 1, overflow: 'hidden' }}>
                        <EventMediaThumb item={item} alt="Vorschaubild" />
                    </Box>
                ))}
            </Box>
        );
    }

    if (previewMedia.length === 3) {
        return (
            <Box
                sx={{
                    width: '100%',
                    aspectRatio: '4 / 3',
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gridTemplateRows: '1fr 1fr',
                    gap: 0.5,
                }}
            >
                <Box sx={{ gridRow: '1 / span 2', borderRadius: 1, overflow: 'hidden' }}>
                    <EventMediaThumb item={previewMedia[0]} alt="Vorschaubild" />
                </Box>
                <Box sx={{ borderRadius: 1, overflow: 'hidden' }}>
                    <EventMediaThumb item={previewMedia[1]} alt="Vorschaubild" />
                </Box>
                <Box sx={{ borderRadius: 1, overflow: 'hidden' }}>
                    <EventMediaThumb item={previewMedia[2]} alt="Vorschaubild" />
                </Box>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                width: '100%',
                aspectRatio: '4 / 3',
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gridTemplateRows: '1fr 1fr',
                gap: 0.5,
            }}
        >
            {previewMedia.slice(0, 4).map((item) => (
                <Box key={item.id} sx={{ borderRadius: 1, overflow: 'hidden' }}>
                    <EventMediaThumb item={item} alt="Vorschaubild" />
                </Box>
            ))}
        </Box>
    );
};

export default EventMediaMosaic;
